import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Accordion,
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
} from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  approveMOMAPI,
  getMOMEditorDataAPI,
  rejectMOMAPI,
  saveMOMDraftAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import StatusBadge from '../../../Components/shared/StatusBadge';
import { companyUrl } from '../../../utils/mhNav';
import styles from './MOMEditor.module.scss';

export default function MOMEditor() {
  const { meetingId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [distributeOpen, setDistributeOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMOMEditorDataAPI(meetingId);
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [meetingId]);

  useEffect(() => {
    load();
  }, [load]);

  const markDirty = () => setDirty(true);

  const save = async (patch) => {
    await saveMOMDraftAPI(meetingId, patch);
    toast.success('Draft saved');
    setDirty(false);
    load();
  };

  const assigneeOpts = useMemo(
    () => (data?.assigneeOptions || []).map((o) => ({ value: o.value, label: o.label })),
    [data]
  );

  const filteredSpeakers = useMemo(() => {
    const q = searchQ.toLowerCase();
    const list = data?.transcriptSpeakers || [];
    if (!q) return list;
    return list.filter((s) => s.preview.toLowerCase().includes(q) || s.speaker.toLowerCase().includes(q));
  }, [data, searchQ]);

  if (loading && !data) {
    return (
      <div>
        <PageHeader title="MOM Editor" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Transcripts', path: companyUrl('transcripts') }, { label: 'MOM' }]} />
        <LoadingSkeleton rows={8} cols={3} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <PageHeader title="MOM Editor" breadcrumbs={[{ label: 'Company', path: companyUrl('dashboard') }, { label: 'Transcripts', path: companyUrl('transcripts') }]} />
        <p className="text-danger">Failed to load.</p>
        <Button onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Minutes of meeting"
        breadcrumbs={[
          { label: 'Company', path: companyUrl('dashboard') },
          { label: 'Transcripts', path: companyUrl('transcripts') },
          { label: data.meetingTitle || 'MOM' },
        ]}
      />

      {dirty && (
        <Alert variant="warning" className={styles.stickyWarn}>
          Unsaved changes — save before leaving.
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={3}>
          <Card>
            <Card.Body>
              <div className="small text-muted">Transcript reference</div>
              <div className="fw-medium">{data.meetingTitle}</div>
              <div className="small">{new Date(data.meetingDate).toLocaleString()}</div>
              <Form.Control className="mt-2" size="sm" placeholder="Search transcript…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
              <div className={`mt-3 ${styles.speakerList}`}>
                {filteredSpeakers.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={styles.speakerItem}
                    onClick={() => document.getElementById('mom-center')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <div className="small text-muted">{s.time}</div>
                    <div className="fw-medium">{s.speaker}</div>
                    <div className="small text-truncate">{s.preview}</div>
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6} id="mom-center">
          <Card>
            <Card.Body>
              <h6 className="text-uppercase text-muted small">Summary</h6>
              <Form.Control as="textarea" rows={3} defaultValue={data.summary} onBlur={(e) => save({ summary: e.target.value })} onChange={markDirty} />

              <h6 className="text-uppercase text-muted small mt-3">Decisions</h6>
              {(data.decisions || []).map((d, i) => (
                <div key={i} className="d-flex gap-2 mb-1">
                  <Form.Control size="sm" defaultValue={d} onBlur={(e) => {
                    const next = [...data.decisions];
                    next[i] = e.target.value;
                    save({ decisions: next });
                  }} onChange={markDirty} />
                  <Button size="sm" variant="outline-danger" onClick={() => {
                    const next = data.decisions.filter((_, j) => j !== i);
                    save({ decisions: next });
                  }}>×</Button>
                </div>
              ))}
              <Button size="sm" variant="light" className="mb-3" onClick={() => save({ decisions: [...(data.decisions || []), 'New decision'] })}>+ Decision</Button>

              <h6 className="text-uppercase text-muted small">Action items</h6>
              {(data.actionItems || []).map((a) => (
                <Row key={a.id} className="g-1 mb-2 align-items-end">
                  <Col md={5}>
                    <Form.Control size="sm" placeholder="What" defaultValue={a.what} onBlur={(e) => {
                      const next = data.actionItems.map((x) => (x.id === a.id ? { ...x, what: e.target.value } : x));
                      save({ actionItems: next });
                    }} onChange={markDirty} />
                  </Col>
                  <Col md={4}>
                    <Select
                      options={assigneeOpts}
                      defaultValue={assigneeOpts.find((o) => o.value === a.assigneeId)}
                      onChange={(opt) => {
                        const next = data.actionItems.map((x) =>
                          x.id === a.id ? { ...x, assigneeId: opt.value, assigneeName: opt.label } : x
                        );
                        save({ actionItems: next });
                      }}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control type="date" size="sm" defaultValue={a.dueDate} onBlur={(e) => {
                      const next = data.actionItems.map((x) => (x.id === a.id ? { ...x, dueDate: e.target.value } : x));
                      save({ actionItems: next });
                    }} onChange={markDirty} />
                  </Col>
                </Row>
              ))}
              <Button size="sm" variant="light" onClick={() => save({
                actionItems: [...(data.actionItems || []), { id: `ai-${Date.now()}`, what: '', assigneeId: 'u-1', assigneeName: 'Sara Al-Hinai', dueDate: new Date().toISOString().slice(0, 10) }],
              })}>Add Action Item</Button>

              <h6 className="text-uppercase text-muted small mt-3">Risks</h6>
              <Form.Control as="textarea" rows={2} defaultValue={data.risks} onBlur={(e) => save({ risks: e.target.value })} onChange={markDirty} />

              <h6 className="text-uppercase text-muted small mt-3">Next steps</h6>
              {(data.nextSteps || []).map((d, i) => (
                <div key={i} className="d-flex gap-2 mb-1">
                  <Form.Control size="sm" defaultValue={d} onBlur={(e) => {
                    const next = [...data.nextSteps];
                    next[i] = e.target.value;
                    save({ nextSteps: next });
                  }} onChange={markDirty} />
                  <Button size="sm" variant="outline-danger" onClick={() => save({ nextSteps: data.nextSteps.filter((_, j) => j !== i) })}>×</Button>
                </div>
              ))}
              <Button size="sm" variant="light" onClick={() => save({ nextSteps: [...(data.nextSteps || []), 'Next step'] })}>+ Step</Button>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3}>
          <Card className="mb-3">
            <Card.Body>
              <div className="small text-muted">Meeting</div>
              <div>{data.meetingTitle}</div>
              <div className="small">{data.organizerName}</div>
              <div className="mt-2 small text-muted">Approver</div>
              <Select
                options={assigneeOpts}
                placeholder="Select approver"
                onChange={(opt) => save({ approverId: opt?.value, approverName: opt?.label })}
              />
              <div className="mt-2">Status: <StatusBadge status={data.status} /></div>
            </Card.Body>
          </Card>
          <Accordion>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Version history</Accordion.Header>
              <Accordion.Body>
                {(data.versions || []).map((v) => (
                  <div key={v.id} className="small mb-2">
                    <Link to="#" onClick={(e) => e.preventDefault()}>{v.label}</Link>
                    <div className="text-muted">{new Date(v.at).toLocaleString()}</div>
                  </div>
                ))}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          <Button className="mt-3 w-100" variant="outline-danger" onClick={() => setRejectOpen(true)}>Reject</Button>
        </Col>
      </Row>

      <div className={styles.bottomBar}>
        <Button variant="success" onClick={() => setDistributeOpen(true)}>Approve & Distribute</Button>
        <Button variant="outline-secondary" className="ms-2" disabled={data.status !== 'approved'} onClick={() => toast.success('Sent to attendees (mock)')}>
          Send to Attendees
        </Button>
        <Button variant="outline-primary" className="ms-2" onClick={() => toast.success('PDF export (mock)')}>Export PDF</Button>
      </div>

      <ConfirmModal
        isOpen={distributeOpen}
        onClose={() => setDistributeOpen(false)}
        title="Send to attendees?"
        message="Send approved MOM to 4 attendees?"
        confirmLabel="Send"
        onConfirm={async () => {
          await approveMOMAPI(meetingId);
          toast.success('Approved');
          setDistributeOpen(false);
          load();
        }}
      />

      <Modal show={rejectOpen} onHide={() => setRejectOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Reject MOM</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Label>Reason *</Form.Label>
          <Form.Control as="textarea" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectOpen(false)}>Close</Button>
          <Button variant="danger" disabled={!rejectReason.trim()} onClick={async () => {
            await rejectMOMAPI(meetingId, rejectReason);
            toast.success('Rejected');
            setRejectOpen(false);
            load();
          }}>Reject</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
